<script lang="ts">
  import { onMount } from 'svelte';
  import { _, locale } from 'svelte-i18n';
  import { navigate } from 'svelte-routing';
  import {
    api,
    type DocumentCatalogEntry,
    type ReportEntryDto,
    type RelatedDocumentsResponse,
  } from '../api';
  import { formatDate } from '../settings';
  import { userSettings } from '../stores/settingsStore';
  import { getLocalizedCombinedCategoryLabel } from '../categoriesLocalization';
  import FileText from 'lucide-svelte/icons/file-text';
  import Link2 from 'lucide-svelte/icons/link-2';
  import ChevronDown from 'lucide-svelte/icons/chevron-down';
  import ChevronRight from 'lucide-svelte/icons/chevron-right';
  import BarChart3 from 'lucide-svelte/icons/bar-chart-3';
  import FileSearch from 'lucide-svelte/icons/file-search';
  import Copy from 'lucide-svelte/icons/copy';
  import { Spinner, Button } from './ui/index';
  import DocumentRowItem from './DocumentRowItem.svelte';

  export let documentId: string;
  export let duplicateOf: string | undefined = undefined;
  export let documentCategory: string | undefined = undefined;
  export let documentSubCategory: string | undefined = undefined;

  const PAGE_SIZE = 5;

  let relatedDocuments: DocumentCatalogEntry[] = [];
  let originalDocument: { id: string; fileName: string } | null = null;
  let reports: ReportEntryDto[] = [];
  let loading = true;
  let loadingMore = false;
  let error: string | null = null;
  let availableCorrelationTypes: string[] = [];
  let hasMore = false;

  // UI State for expansion
  let expandedGroups: Record<string, boolean> = {
    duplicate: true,
    similar: true,
    reports: true,
  };
  let expandedSubGroups: Record<string, boolean> = {
    'similar:all': true,
  };

  async function loadData() {
    loading = true;
    error = null;

    try {
      const promises: [
        Promise<RelatedDocumentsResponse>,
        Promise<ReportEntryDto[]>,
        Promise<{ id: string; fileName: string } | null>?,
      ] = [
        api.reports.getRelatedDocuments(documentId, undefined, PAGE_SIZE, 0),
        api.reports.getDocumentReports(documentId),
      ];
      if (duplicateOf) {
        promises.push(api.documents.getDocument(duplicateOf).catch(() => null));
      }

      const [relatedResponse, reportsResponse, origDoc] = await Promise.all(promises);

      relatedDocuments = relatedResponse.documents;
      availableCorrelationTypes = relatedResponse.availableCorrelationTypes;
      hasMore = relatedResponse.hasMore;
      reports = reportsResponse;
      originalDocument = origDoc ? { id: origDoc.id, fileName: origDoc.fileName } : null;

      // Initialize subgroup expansion states
      availableCorrelationTypes.forEach((type) => {
        const key = `similar:${type}`;
        if (expandedSubGroups[key] === undefined) {
          expandedSubGroups[key] = false;
        }
      });
    } catch (err) {
      error = err instanceof Error ? err.message : String($_('messages.error'));
    } finally {
      loading = false;
    }
  }

  async function loadMore() {
    loadingMore = true;
    try {
      const response = await api.reports.getRelatedDocuments(
        documentId,
        undefined,
        PAGE_SIZE,
        relatedDocuments.length,
      );
      relatedDocuments = [...relatedDocuments, ...response.documents];
      hasMore = response.hasMore;
    } catch (err) {
      // silently fail — existing results stay visible
    } finally {
      loadingMore = false;
    }
  }

  function toggleGroup(id: string) {
    expandedGroups[id] = !expandedGroups[id];
  }

  function toggleSubGroup(id: string) {
    expandedSubGroups[id] = !expandedSubGroups[id];
  }

  function navigateToDocument(doc: DocumentCatalogEntry) {
    const identifier = doc.slug || doc.id;
    navigate(`/documents/${identifier}`);
  }

  function navigateToReport(report: ReportEntryDto) {
    // Construct report URL: /reports/{type}?year={year}&category={category}...
    const params = new URLSearchParams();
    if (report.year) params.set('year', report.year.toString());
    if (report.category) params.set('category', report.category);

    navigate(`/reports/${report.reportType}?${params.toString()}`);
  }

  function formatLocalizedCategory(doc: DocumentCatalogEntry): string {
    if (!doc.category) return '-';
    return getLocalizedCombinedCategoryLabel(doc.category, doc.subCategory ?? null, $locale);
  }

  // Filter similar documents by category/subcategory
  $: sameCategoryDocs = documentCategory
    ? relatedDocuments.filter((d) => d.category === documentCategory)
    : [];
  $: sameSubCategoryDocs =
    documentSubCategory && documentCategory
      ? relatedDocuments.filter(
          (d) => d.category === documentCategory && d.subCategory === documentSubCategory,
        )
      : [];

  // Group reports by type and year
  $: groupedReports = reports.reduce(
    (acc, report) => {
      const typeKey = report.reportType;
      if (!acc[typeKey]) acc[typeKey] = {};

      const yearKey = report.year?.toString() || 'unknown';
      if (!acc[typeKey][yearKey]) acc[typeKey][yearKey] = [];

      acc[typeKey][yearKey].push(report);
      return acc;
    },
    {} as Record<string, Record<string, ReportEntryDto[]>>,
  );

  onMount(() => {
    loadData();
  });
</script>

<div class="bg-surface border border-border rounded-lg p-4">
  <h3 class="flex items-center gap-2 text-sm font-semibold text-text mb-4">
    <Link2 class="w-4 h-4 text-accent" strokeWidth={1.5} />
    {$_('components.related_documents.title')}
  </h3>

  {#if loading}
    <div class="flex justify-center py-8">
      <Spinner size="md" label={$_('messages.loading')} />
    </div>
  {:else if error}
    <div class="text-center py-8">
      <p class="text-sm text-error mb-2">{error}</p>
      <Button variant="primary" size="sm" on:click={loadData}>
        {$_('pages.home.retry')}
      </Button>
    </div>
  {:else}
    <div class="flex flex-col gap-2">
      <!-- GROUP: Duplicate Of -->
      {#if duplicateOf}
        <div data-testid="doc-detail-duplicate-banner" class="flex flex-col">
          <button
            class="flex items-center gap-2 w-full text-left py-1.5 px-2 hover:bg-bg-alt rounded transition-colors"
            on:click={() => toggleGroup('duplicate')}
          >
            {#if expandedGroups['duplicate']}
              <ChevronDown class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
            {:else}
              <ChevronRight class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
            {/if}
            <Copy class="w-4 h-4 text-warning" strokeWidth={1.5} />
            <span class="text-sm font-medium text-text"
              >{$_('components.related_documents.group_duplicate')}</span
            >
          </button>

          {#if expandedGroups['duplicate']}
            <div class="ml-6 flex flex-col gap-1 mt-1 border-l border-warning/40 pl-2">
              <button
                class="flex items-center gap-2 w-full text-left py-1.5 px-2 hover:bg-accent/10 rounded text-xs group transition-colors"
                on:click={() => navigate(`/documents/${duplicateOf}`)}
              >
                <FileText class="w-3 h-3 text-warning" strokeWidth={1.5} />
                <span class="truncate text-text group-hover:text-accent font-medium">
                  {originalDocument?.fileName || duplicateOf}
                </span>
                <span class="text-[10px] text-text-muted ml-auto"
                  >{$_('components.related_documents.original')}</span
                >
              </button>
            </div>
          {/if}
        </div>
      {/if}

      <!-- GROUP: Similar Documents -->
      <div class="flex flex-col">
        <button
          class="flex items-center gap-2 w-full text-left py-1.5 px-2 hover:bg-bg-alt rounded transition-colors"
          on:click={() => toggleGroup('similar')}
        >
          {#if expandedGroups['similar']}
            <ChevronDown class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
          {:else}
            <ChevronRight class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
          {/if}
          <FileSearch class="w-4 h-4 text-accent" strokeWidth={1.5} />
          <span class="text-sm font-medium text-text"
            >{$_('components.related_documents.group_similar')}</span
          >
          <span class="text-xs text-text-muted ml-auto"
            >({relatedDocuments.length}{hasMore ? '+' : ''})</span
          >
        </button>

        {#if expandedGroups['similar']}
          <div class="ml-6 flex flex-col gap-1 mt-1 border-l border-border/50 pl-2">
            <!-- Subgroup: All -->
            <div class="flex flex-col">
              <button
                class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-bg-alt rounded text-xs transition-colors"
                on:click={() => toggleSubGroup('similar:all')}
              >
                {#if expandedSubGroups['similar:all']}
                  <ChevronDown class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                {:else}
                  <ChevronRight class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                {/if}
                <span class="text-text-secondary">{$_('components.related_documents.all')}</span>
                <span class="text-[10px] text-text-muted"
                  >({relatedDocuments.length}{hasMore ? '+' : ''})</span
                >
              </button>

              {#if expandedSubGroups['similar:all']}
                <div class="ml-4 flex flex-col gap-0.5 mt-0.5">
                  {#each relatedDocuments as doc (doc.id)}
                    <DocumentRowItem
                      {doc}
                      fallbackLabel={formatLocalizedCategory(doc)}
                      onClick={() => navigateToDocument(doc)}
                    />
                  {/each}
                  {#if relatedDocuments.length === 0}
                    <p class="px-2 py-1 text-[11px] text-text-muted italic">
                      {$_('components.related_documents.no_related_documents')}
                    </p>
                  {/if}
                  {#if hasMore}
                    <button
                      class="flex items-center justify-center gap-1.5 w-full py-1.5 px-2 mt-1 text-xs text-accent hover:text-accent-hover hover:bg-accent/10 rounded transition-colors disabled:opacity-50"
                      on:click={loadMore}
                      disabled={loadingMore}
                    >
                      {#if loadingMore}
                        <Spinner size="sm" />
                      {/if}
                      {$_('components.related_documents.show_more')}
                    </button>
                  {/if}
                </div>
              {/if}
            </div>

            <!-- Subgroup: Same Category -->
            {#if documentCategory && sameCategoryDocs.length > 0}
              <div class="flex flex-col">
                <button
                  class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-bg-alt rounded text-xs transition-colors"
                  on:click={() => toggleSubGroup('similar:category')}
                >
                  {#if expandedSubGroups['similar:category']}
                    <ChevronDown class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {:else}
                    <ChevronRight class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {/if}
                  <span class="text-text-secondary"
                    >{$_('components.related_documents.same_category')}</span
                  >
                  <span class="text-[10px] text-text-muted">({sameCategoryDocs.length})</span>
                </button>

                {#if expandedSubGroups['similar:category']}
                  <div class="ml-4 flex flex-col gap-0.5 mt-0.5">
                    {#each sameCategoryDocs as doc (doc.id)}
                      <DocumentRowItem {doc} onClick={() => navigateToDocument(doc)} />
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}

            <!-- Subgroup: Same Subcategory -->
            {#if documentSubCategory && sameSubCategoryDocs.length > 0}
              <div class="flex flex-col">
                <button
                  class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-bg-alt rounded text-xs transition-colors"
                  on:click={() => toggleSubGroup('similar:subcategory')}
                >
                  {#if expandedSubGroups['similar:subcategory']}
                    <ChevronDown class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {:else}
                    <ChevronRight class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {/if}
                  <span class="text-text-secondary"
                    >{$_('components.related_documents.same_subcategory')}</span
                  >
                  <span class="text-[10px] text-text-muted">({sameSubCategoryDocs.length})</span>
                </button>

                {#if expandedSubGroups['similar:subcategory']}
                  <div class="ml-4 flex flex-col gap-0.5 mt-0.5">
                    {#each sameSubCategoryDocs as doc (doc.id)}
                      <DocumentRowItem {doc} onClick={() => navigateToDocument(doc)} />
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}

            <!-- Subgroups: by Correlation Type -->
            {#each availableCorrelationTypes as type (type)}
              <div class="flex flex-col">
                <button
                  class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-bg-alt rounded text-xs transition-colors"
                  on:click={() => toggleSubGroup(`similar:${type}`)}
                >
                  {#if expandedSubGroups[`similar:${type}`]}
                    <ChevronDown class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {:else}
                    <ChevronRight class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {/if}
                  <span class="text-text-secondary"
                    >{$_(`components.related_documents.${type}`)}</span
                  >
                  <span class="text-[10px] text-text-muted"
                    >({relatedDocuments.filter((d) =>
                      d.correlationKeys?.some((k) => k.startsWith(type + ':')),
                    ).length})</span
                  >
                </button>

                {#if expandedSubGroups[`similar:${type}`]}
                  <div class="ml-4 flex flex-col gap-0.5 mt-0.5">
                    {#each relatedDocuments.filter( (d) => d.correlationKeys?.some( (k) => k.startsWith(type + ':') ) ) as doc (doc.id)}
                      <DocumentRowItem {doc} onClick={() => navigateToDocument(doc)} />
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <!-- GROUP: Reports -->
      <div class="flex flex-col mt-2">
        <button
          class="flex items-center gap-2 w-full text-left py-1.5 px-2 hover:bg-bg-alt rounded transition-colors"
          on:click={() => toggleGroup('reports')}
        >
          {#if expandedGroups['reports']}
            <ChevronDown class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
          {:else}
            <ChevronRight class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
          {/if}
          <BarChart3 class="w-4 h-4 text-accent" strokeWidth={1.5} />
          <span class="text-sm font-medium text-text"
            >{$_('components.related_documents.group_reports')}</span
          >
          <span class="text-xs text-text-muted ml-auto">({reports.length})</span>
        </button>

        {#if expandedGroups['reports']}
          <div class="ml-6 flex flex-col gap-1 mt-1 border-l border-border/50 pl-2">
            {#each Object.entries(groupedReports) as [type, years] (type)}
              <div class="flex flex-col">
                <button
                  class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-bg-alt rounded text-xs transition-colors"
                  on:click={() => toggleSubGroup(`report:${type}`)}
                >
                  {#if expandedSubGroups[`report:${type}`]}
                    <ChevronDown class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {:else}
                    <ChevronRight class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                  {/if}
                  <span class="text-text-secondary uppercase tracking-wider">{type}</span>
                </button>

                {#if expandedSubGroups[`report:${type}`] || expandedSubGroups[`report:${type}`] === undefined}
                  <div class="ml-4 flex flex-col gap-1 mt-0.5">
                    {#each Object.entries(years) as [year, entries] (year)}
                      <div class="flex flex-col">
                        {#each entries as entry (entry.id)}
                          <button
                            class="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-accent/10 rounded text-xs group transition-colors"
                            on:click={() => navigateToReport(entry)}
                          >
                            <BarChart3 class="w-3 h-3 text-text-muted" strokeWidth={1.5} />
                            <span class="truncate text-text group-hover:text-accent">
                              {year} - {entry.taxCategory || entry.category}
                            </span>
                            <span class="text-[10px] text-text-muted ml-auto"
                              >{formatDate(entry.entryDate, $userSettings)}</span
                            >
                          </button>
                        {/each}
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}

            {#if reports.length === 0}
              <p class="px-2 py-1 text-[11px] text-text-muted italic">
                {$_('components.related_documents.no_reports')}
              </p>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
